import { Action, Dispatch } from 'redux';
import { History } from 'history';
import { ThunkDispatch } from 'redux-thunk';
import { AxiosError } from 'axios';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { showSuccessMessage, showError } from 'shared/toaster';
import { SYSTEM_CONFIGURATION_ROUTE, SYSTEM, REGISTERED_NODES } from '../../constants';
import { extractErrorMessage } from '../../utils';
import * as api from './registeredNode.api';
import {
  RegisteredNode,
  GetRegisteredNodeAction,
  GetSingleRegisteredNodeAction,
  SelectRegisteredNode,
  DeleteRegisteredNodeThunkAction,
  CreateRegisteredNodeThunkAction,
  UpdateRegisteredNodeThunkAction,
  GET_REGISTERED_NODES,
  GET_REGISTERED_NODE,
  GO_TO_REGISTERED_NODE_CREATE,
  GO_TO_REGISTERED_NODE_EDIT,
  GO_TO_REGISTERED_NODE_VIEW,
  GO_TO_REGISTERED_NODE_LIST,
  CREATE_REGISTERED_NODE,
  DELETE_REGISTERED_NODE,
  UPDATE_REGISTERED_NODE,
  SELECT_REGISTERED_NODE,
  RegisteredNodeServerResponse,
} from './registeredNode.types';
import { getSelectedRegisteredNode } from './registeredNode.selectors';

export function getRegisteredNodes(deliveryFunctionId: string): GetRegisteredNodeAction {
  return {
    type: GET_REGISTERED_NODES,
    payload: api.getRegisteredNodes(deliveryFunctionId),
  };
}

export function getRegisteredNode(deliveryFunctionId: string, registeredNodeId: string): GetSingleRegisteredNodeAction {
  return {
    type: GET_REGISTERED_NODE,
    payload: api.getRegisteredNode(deliveryFunctionId, registeredNodeId),
  };
}

export function createRegisteredNode(
  deliveryFunctionId: string,
  body: WithAuditDetails<RegisteredNodeServerResponse>,
  history: History,
): CreateRegisteredNodeThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_REGISTERED_NODE,
        payload: api.createRegisteredNode(deliveryFunctionId, body),
      });

      dispatch(goToRegisteredNodeList(history));
      showSuccessMessage(i18n.t('registeredNode.create.success', { name: body.data.NODENAME }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('registeredNode.create.failed', { name: body.data.NODENAME, error: errorMessage }));
    }
  };
}

export function updateRegisteredNode(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<RegisteredNodeServerResponse>,
  history: History,
): UpdateRegisteredNodeThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_REGISTERED_NODE,
        payload: api.updateRegisteredNode(deliveryFunctionId, id, body),
      });

      dispatch(goToRegisteredNodeList(history));
      showSuccessMessage(i18n.t('registeredNode.edit.success', { name: body.data.NODENAME }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('registeredNode.edit.failed', { name: body.data.NODENAME, error: errorMessage }));
    }
  };
}

export function deleteRegisteredNode(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteRegisteredNodeThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedRegisteredNode] = getSelectedRegisteredNode(getState());

    try {
      await dispatch({
        type: DELETE_REGISTERED_NODE,
        payload: api.deleteRegisteredNode(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('registeredNode.delete.success', { name: selectedRegisteredNode?.nodeName }), 5000);
      if (history != null) {
        dispatch(goToRegisteredNodeList(history));
      }
    } catch (e) {
      showError(i18n.t('registeredNode.delete.failed', { name: selectedRegisteredNode?.nodeName }));

    } finally {
      dispatch(getRegisteredNodes(deliveryFunctionId));
    }
  };
}

export function selectRegisteredNode(
  registeredNode: RegisteredNode[],
): SelectRegisteredNode {
  return {
    type: SELECT_REGISTERED_NODE,
    payload: registeredNode,
  };
}

export function goToRegisteredNodeList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_REGISTERED_NODE_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${REGISTERED_NODES}`);
  };
}

export function goToRegisteredNodeCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_REGISTERED_NODE_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${REGISTERED_NODES}/create`);
  };
}

export function goToRegisteredNodeEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_REGISTERED_NODE_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${REGISTERED_NODES}/edit`);
  };
}

export function goToRegisteredNodeView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_REGISTERED_NODE_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${REGISTERED_NODES}/view`);
  };
}
